import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";

const empty = {
  match: "",
  home_team: "",
  away_team: "",
  league: "",
  category: "",
  season: "",
  players: [],
  market: "",
  selection: "",
  bet_type: "single",
  bookmaker: "",
  stake: 0,
  odds: 0,
  return_amount: 0,
  result: "pending",
  bet_date: new Date().toISOString().slice(0, 10),
  notes: "",
  image_base64: null,
};

const MARKETS = [
  "Match Result",
  "Both Teams To Score",
  "Over/Under 2.5",
  "Over/Under Goals",
  "Anytime Goalscorer",
  "Correct Score",
  "Double Chance",
  "Handicap",
  "Draw No Bet",
  "Multi / Accumulator",
  "Other",
];

export default function BetDialog({ open, onClose, initial, betId, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [cats, setCats] = useState([]);
  const [seasons, setSeasons] = useState([]);

  useEffect(() => {
    if (open) {
      api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
      api.get("/bets/meta/seasons").then((r) => setSeasons(r.data)).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      const base = { ...empty, ...(initial || {}) };
      base.players = Array.isArray(base.players) ? base.players : [];
      if (!base.bet_date) base.bet_date = new Date().toISOString().slice(0, 10);
      setForm(base);
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        stake: Number(form.stake) || 0,
        odds: Number(form.odds) || 0,
        return_amount: Number(form.return_amount) || 0,
        players:
          typeof form.players === "string"
            ? form.players.split(",").map((s) => s.trim()).filter(Boolean)
            : form.players,
      };
      if (betId) await api.put(`/bets/${betId}`, payload);
      else await api.post("/bets", payload);
      toast.success(betId ? "Bet updated" : "Bet saved");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const field = (label, key, props = {}) => (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input
        value={form[key] ?? ""}
        onChange={(e) => set(key, e.target.value)}
        data-testid={`bet-${key}`}
        className="rounded-xl h-10"
        {...props}
      />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl"
        data-testid="bet-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {betId ? "Edit Bet" : "Review & Save Bet"}
          </DialogTitle>
          <DialogDescription>
            {betId ? "Update this bet's details." : "Check the extracted details, then save."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          {field("Match", "match", { className: "rounded-xl h-10 col-span-2" })}
          {field("Home Team", "home_team")}
          {field("Away Team", "away_team")}
          {field("League", "league")}
          {field("Bookmaker", "bookmaker")}

          <div className="space-y-1.5 col-span-2">
            <Label className="text-xs text-muted-foreground">
              Competition / Category
            </Label>
            <Input
              value={form.category ?? ""}
              onChange={(e) => set("category", e.target.value)}
              data-testid="bet-category"
              list="category-suggestions"
              placeholder="e.g. World Cup 2026, Premier League"
              className="rounded-xl h-10"
            />
            <datalist id="category-suggestions">
              {cats.map((c) => (
                <option key={c.id} value={c.name} />
              ))}
            </datalist>
          </div>

          <div className="space-y-1.5 col-span-2">
            <Label className="text-xs text-muted-foreground">Season</Label>
            <Input
              value={form.season ?? ""}
              onChange={(e) => set("season", e.target.value)}
              data-testid="bet-season"
              list="season-suggestions"
              placeholder="e.g. 2025/26 or 2026"
              className="rounded-xl h-10"
            />
            <datalist id="season-suggestions">
              {seasons.map((sn) => (
                <option key={sn} value={sn} />
              ))}
            </datalist>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Market</Label>
            <Select value={form.market || ""} onValueChange={(v) => set("market", v)}>
              <SelectTrigger className="rounded-xl h-10" data-testid="bet-market">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {MARKETS.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {field("Selection", "selection")}

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Bet Type</Label>
            <Select value={form.bet_type} onValueChange={(v) => set("bet_type", v)}>
              <SelectTrigger className="rounded-xl h-10" data-testid="bet-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="single">Single</SelectItem>
                <SelectItem value="multi">Multi / Acca</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Result</Label>
            <Select value={form.result} onValueChange={(v) => set("result", v)}>
              <SelectTrigger className="rounded-xl h-10" data-testid="bet-result">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="win">Win</SelectItem>
                <SelectItem value="loss">Loss</SelectItem>
                <SelectItem value="void">Void</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {field("Stake (A$)", "stake", { type: "number", step: "0.01" })}
          {field("Odds", "odds", { type: "number", step: "0.01" })}
          {field("Return (A$)", "return_amount", { type: "number", step: "0.01" })}
          {field("Date", "bet_date", { type: "date" })}

          <div className="space-y-1.5 col-span-2">
            <Label className="text-xs text-muted-foreground">
              Players (comma separated)
            </Label>
            <Input
              value={
                Array.isArray(form.players) ? form.players.join(", ") : form.players || ""
              }
              onChange={(e) => set("players", e.target.value)}
              data-testid="bet-players"
              className="rounded-xl h-10"
            />
          </div>

          <div className="space-y-1.5 col-span-2">
            <Label className="text-xs text-muted-foreground">Notes</Label>
            <Textarea
              value={form.notes || ""}
              onChange={(e) => set("notes", e.target.value)}
              data-testid="bet-notes"
              className="rounded-xl"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} className="rounded-full">
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="bet-save"
            className="rounded-full font-semibold px-6"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {betId ? "Update Bet" : "Save Bet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
